import React, { useState, useCallback } from "react";
import { Avatar, Button } from "antd";
import {
  UserPlus,
  UserMinus,
  Edit3,
  Video,
  Music,
  FileText,
  FileSpreadsheet,
  Presentation,
  Archive,
  Paperclip,
} from "lucide-react";
import {
  ChatMessage,
  isTextMessage,
  isParticipantAddedMessage,
  isParticipantRemovedMessage,
  isTopicUpdatedMessage,
  isDeletedMessage,
  getMessageContentType,
  TextMessage as TextMessageType,
  ImageMetadata,
  VideoMetadata,
  AlbumMetadata,
  FileMetadata,
  LinkPreviewMetadata,
  LocationMetadata,
  MessageReaction,
  ReplyMetadata,
} from "../../../types/chat";

import TextMessage from "../TextMessage";
import ImageMessage from "../ImageMessage";
import VideoMessage from "../VideoMessage";
import AlbumMessage from "../AlbumMessage";
import FileMessage from "../FileMessage";
import LinkMessage from "../LinkMessage";
import LocationMessage from "../LocationMessage";
import { MessageActions } from "../../MessageActions";
import ReactionDisplay from "../../ReactionDisplay";
import ReactionDetailsModal from "../../ReactionDetailsModal";
import { ReplyState } from "../../../utils/replyUtils";
import { formatMentionsForDisplayText } from "../../../utils/messageUtils";
import { useConfirmMessage } from "../../../hooks/mutations/chat";
import ConfirmationDetailsModal from "../../ConfirmationDetailsModal";
import ReaderListModal from "../../ReaderListModal";

import styles from "./MessageRenderer.module.scss";

interface MessageRendererProps {
  message: ChatMessage;
  isOwnMessage?: boolean;
  senderAvatar?: string | null;
  roomId?: string;
  threadId?: string;
  onReply?: (replyState: ReplyState) => void;
  onScrollToMessage?: (messageId: string) => void;
  reaction?: MessageReaction;
  isPinned?: boolean;
  currentUserId?: string; // CUI for message ownership check
  actualUserId?: string; // Real user ID for confirmation check
  roomType?: string; // Room type to check if Channel (C)
  isAdmin?: boolean; // Check if current user is admin
  isConfirmed?: boolean;
}

const MessageRenderer: React.FC<MessageRendererProps> = ({
  message,
  isOwnMessage = false,
  senderAvatar,
  roomId,
  threadId,
  onReply,
  onScrollToMessage,
  reaction,
  isPinned = false,
  currentUserId,
  actualUserId,
  roomType,
  isAdmin = false,
  isConfirmed = false,
}) => {
  const [hover, setHover] = useState(false);
  const [showReactionDetails, setShowReactionDetails] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [showConfirmationDetails, setShowConfirmationDetails] = useState(false);
  const [showReaderList, setShowReaderList] = useState(false);

  // Hook to confirm message
  const { mutate: confirmMessage } = useConfirmMessage();

  /** ==========================
   * REPLY HANDLER
   =========================== */
  const handleReply = useCallback(() => {
    if (!onReply) return;
    onReply({
      messageId: message.id || "",
      senderName: message.senderDisplayName || "",
      content: message.content?.message || "",
      message,
    });
  }, [onReply, message]);

  /** ==========================
   * SCROLL TO ORIGINAL MESSAGE HANDLER
   =========================== */
  const handleScrollToOriginal = useCallback(() => {
    const metadata = message.metadata as any;
    const replyMessageId = metadata?.replyMessageId;
    if (onScrollToMessage && replyMessageId) {
      onScrollToMessage(replyMessageId);
    }
  }, [onScrollToMessage, message.metadata]);

  /** ==========================
   * CONFIRMATION HANDLER
   =========================== */
  const handleConfirm = useCallback(() => {
    if (!threadId || !message.id || isConfirming) return;

    setIsConfirming(true);

    confirmMessage(
      {
        messageId: message.id,
        threadId: threadId,
      },
      {
        onSuccess: () => {
          console.log("✅ Message confirmed successfully");
          setIsConfirming(false);
        },
        onError: (error) => {
          console.error("❌ Error confirming message:", error);
          setIsConfirming(false);
        },
      },
    );
  }, [threadId, message.id, isConfirming, confirmMessage]);

  /** ==========================
   * DETECT TYPE & METADATA (before early returns)
   =========================== */
  const type = isTextMessage(message) ? getMessageContentType(message as TextMessageType) : null;
  const metadata = message.metadata as any;
  const hasReply = metadata?.hasReply === "true";
  const replyMetadata = hasReply ? (metadata as ReplyMetadata) : null;
  const needConfirmed = metadata?.needConfirmed === true || metadata?.needConfirmed === "true";

  // Check if this is a Channel room (confirmation only for Channel rooms)
  const isChannelRoom = roomType === "C";

  // Need confirmation check:
  // 1. Message requires confirmation (needConfirmed=true)
  // 2. Room is channel (isChannelRoom=true)
  // 3. Current user has NOT confirmed yet (isConfirmed=false)
  const shouldShowConfirmButton = isChannelRoom && needConfirmed && !isConfirmed;

  // Debug: Log confirmation status
  if (needConfirmed) {
    console.log("🔍 Confirmation status:", {
      messageId: message.id,
      needConfirmed,
      isChannelRoom,
      isConfirmed,
      shouldShowConfirmButton,
    });
  }

  /** ==========================
   * SYSTEM MESSAGE BRANCHES
   =========================== */
  if (isParticipantAddedMessage(message)) {
    return (
      <div className={styles.systemMessage}>
        <UserPlus size={16} className={styles.systemIcon} />
        <span className={styles.systemText}>Đã thêm thành viên mới</span>
      </div>
    );
  }

  if (isParticipantRemovedMessage(message)) {
    return (
      <div className={styles.systemMessage}>
        <UserMinus size={16} className={styles.systemIcon} />
        <span className={styles.systemText}>Đã xóa thành viên khỏi nhóm</span>
      </div>
    );
  }

  if (isTopicUpdatedMessage(message)) {
    return (
      <div className={styles.systemMessage}>
        <Edit3 size={16} className={styles.systemIcon} />
        <span className={styles.systemText}>
          Tên nhóm mới: <strong>{message.content?.topic}</strong>
        </span>
      </div>
    );
  }

  /** ==========================
   * SCORE BUBBLE CLASS
   =========================== */
  const bubbleClass = !type || type === "text" ? styles.messageBubble : `${styles.messageBubble} ${styles.noPadding}`;

  /** ==========================
   * DELETED MESSAGE
   =========================== */
  if (isDeletedMessage(message)) {
    return (
      <div className={`${styles.messageWrapper} ${isOwnMessage ? styles.own : styles.other}`}>
        {/* Hiển thị avatar cho tin nhắn của người khác */}
        {!isOwnMessage && (
          <div className={styles.avatarContainer}>
            <Avatar src={senderAvatar} size={32}>
              {!senderAvatar && message.senderDisplayName?.charAt(0)}
            </Avatar>
          </div>
        )}
        <div className={styles.messageContainer}>
          {/* Hiển thị tên người gửi cho tin nhắn của người khác */}
          {!isOwnMessage && message.senderDisplayName && (
            <div className={styles.senderName}>{message.senderDisplayName}</div>
          )}
          {/* Khung tin nhắn giống như các tin nhắn bình thường */}
          <div className={styles.messageBubble}>
            <div className={styles.deletedMessage}>
              <i>Tin nhắn đã xóa</i>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /** ==========================
   * NORMAL MESSAGE RENDER
   =========================== */
  return (
    <div
      className={`${styles.messageWrapper} ${isOwnMessage ? styles.own : styles.other}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {!isOwnMessage && (
        <div className={styles.avatarContainer}>
          <Avatar src={senderAvatar} size={32}>
            {!senderAvatar && message.senderDisplayName?.charAt(0)}
          </Avatar>
        </div>
      )}

      <div className={styles.messageContainer}>
        {!isOwnMessage && message.senderDisplayName && (
          <div className={styles.senderName}>{message.senderDisplayName}</div>
        )}

        <div style={{ position: "relative" }}>
          <div className={bubbleClass}>
            {/* Reply Preview rendered here for all message types */}
            {hasReply && replyMetadata && (
              <div className={styles.replyPreview} onClick={handleScrollToOriginal} style={{ cursor: "pointer" }}>
                <div className={styles.replyBar} />
                <div className={styles.replyContent}>
                  <div className={styles.replySender}>{replyMetadata.replySenderName}</div>
                  <div className={styles.replyMessage}>
                    {(() => {
                      const msg = replyMetadata.replyMessage || "";
                      const thumbnailUrl = (replyMetadata as any).replyThumbnailUrl || "";

                      // Có thumbnailUrl -> hiển thị thumbnail
                      if (thumbnailUrl) {
                        // Xác định loại file để hiển thị label
                        let label = "File đính kèm";
                        if (msg.match(/\.(jpg|jpeg|png|webp|gif|bmp|svg)/i)) label = "Hình ảnh";
                        else if (msg.match(/\.(mp4|webm|mov|avi|mkv|flv)/i)) label = "Video";
                        else if (msg.match(/\.(mp3|wav|ogg|m4a|aac|flac)/i)) label = "Âm thanh";
                        else if (msg.match(/\.pdf/i)) label = "Tài liệu PDF";
                        else if (msg.match(/\.(doc|docx)/i)) label = "Tài liệu Word";
                        else if (msg.match(/\.(xls|xlsx)/i)) label = "Bảng tính Excel";
                        else if (msg.match(/\.(ppt|pptx)/i)) label = "Trình chiếu";
                        else if (msg.match(/\.(zip|rar|7z|tar|gz)/i)) label = "File nén";

                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <img
                              src={thumbnailUrl}
                              alt=""
                              style={{ width: 40, height: 40, borderRadius: 4, objectFit: "cover" }}
                            />
                            <span style={{ color: "#666" }}>{label}</span>
                          </div>
                        );
                      }

                      // Không có thumbnailUrl nhưng là link media -> dùng msg làm src
                      if (msg.match(/\.(jpg|jpeg|png|webp|gif|bmp|svg)/i)) {
                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <img
                              src={msg}
                              alt=""
                              style={{ width: 40, height: 40, borderRadius: 4, objectFit: "cover" }}
                            />
                            <span style={{ color: "#666" }}>Hình ảnh</span>
                          </div>
                        );
                      }

                      // Video không có thumbnail -> icon
                      if (msg.match(/\.(mp4|webm|mov|avi|mkv|flv)/i)) {
                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <Video size={24} color="#1780e2" />
                            <span style={{ color: "#666" }}>Video</span>
                          </div>
                        );
                      }

                      // Audio -> icon
                      if (msg.match(/\.(mp3|wav|ogg|m4a|aac|flac)/i)) {
                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <Music size={24} color="#1780e2" />
                            <span style={{ color: "#666" }}>Âm thanh</span>
                          </div>
                        );
                      }

                      // Files -> icon
                      if (msg.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|zip|rar|7z)/i)) {
                        let IconComponent: any = Paperclip;
                        let label = "File đính kèm";
                        if (msg.match(/\.pdf/i)) {
                          IconComponent = FileText;
                          label = "Tài liệu PDF";
                        } else if (msg.match(/\.(doc|docx)/i)) {
                          IconComponent = FileText;
                          label = "Tài liệu Word";
                        } else if (msg.match(/\.(xls|xlsx)/i)) {
                          IconComponent = FileSpreadsheet;
                          label = "Bảng tính Excel";
                        } else if (msg.match(/\.(ppt|pptx)/i)) {
                          IconComponent = Presentation;
                          label = "Trình chiếu";
                        } else if (msg.match(/\.(zip|rar|7z)/i)) {
                          IconComponent = Archive;
                          label = "File nén";
                        }

                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <IconComponent size={24} color="#1780e2" />
                            <span style={{ color: "#666" }}>{label}</span>
                          </div>
                        );
                      }

                      // Text bình thường - format mentions trước, sau đó mới truncate
                      const formattedMsg = formatMentionsForDisplayText(msg);
                      const displayText =
                        formattedMsg.length > 50 ? formattedMsg.substring(0, 50) + "..." : formattedMsg;
                      return displayText;
                    })()}
                  </div>
                </div>
              </div>
            )}

            {type === "text" && <TextMessage message={message as TextMessageType} isOwnMessage={isOwnMessage} />}

            {type === "image" && (
              <ImageMessage
                message={message as TextMessageType}
                metadata={message.metadata as ImageMetadata}
                isOwnMessage={isOwnMessage}
              />
            )}

            {type === "video" && (
              <VideoMessage
                message={message as TextMessageType}
                metadata={message.metadata as VideoMetadata}
                isOwnMessage={isOwnMessage}
              />
            )}

            {type === "album" && (
              <AlbumMessage
                message={message as TextMessageType}
                metadata={message.metadata as AlbumMetadata}
                isOwnMessage={isOwnMessage}
              />
            )}

            {type === "file" && (
              <FileMessage
                message={message as TextMessageType}
                metadata={message.metadata as FileMetadata}
                isOwnMessage={isOwnMessage}
              />
            )}

            {type === "link" && (
              <LinkMessage
                message={message as TextMessageType}
                metadata={message.metadata as LinkPreviewMetadata}
                isOwnMessage={isOwnMessage}
              />
            )}

            {type === "location" && (
              <LocationMessage
                message={message as TextMessageType}
                metadata={message.metadata as LocationMetadata}
                isOwnMessage={isOwnMessage}
              />
            )}

            {/* Footer inside bubble: Confirm button + Timestamp */}
            <div className={styles.bubbleFooter}>
              {shouldShowConfirmButton && (
                <Button
                  type="primary"
                  size="small"
                  className={styles.confirmButton}
                  onClick={handleConfirm}
                  loading={isConfirming}
                  disabled={isConfirming}
                >
                  {isConfirming ? "Đang..." : "Xác nhận"}
                </Button>
              )}
              <span className={styles.timestamp}>
                {new Date(message.createdOn).toLocaleTimeString("vi-VN", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
              {message.editedOn && <span className={styles.edited}>(đã sửa)</span>}
            </div>
          </div>

          {/* Reactions - outside bubble, positioned at right */}
          <div className={styles.reactionContainer}>
            <ReactionDisplay
              messageId={message.id}
              roomId={roomId || ""}
              totalReactions={reaction?.TotalReactions || 0}
              myReaction={reaction?.MyReaction || null}
              otherReactions={reaction?.OtherReaction}
              onShowDetails={() => setShowReactionDetails(true)}
            />
          </div>

          {hover && roomId && (
            <div className={styles.actionsContainer}>
              <MessageActions
                message={message}
                isOwnMessage={isOwnMessage}
                roomId={roomId}
                threadId={threadId}
                onReply={handleReply}
                onShowReactions={() => setShowReactionDetails(true)}
                onShowConfirmations={() => setShowConfirmationDetails(true)}
                onShowReaders={() => setShowReaderList(true)}
                needConfirmed={needConfirmed}
                isChannelRoom={isChannelRoom}
                isPinned={isPinned}
                isAdmin={isAdmin}
              />
            </div>
          )}
        </div>

        {showReactionDetails && (
          <ReactionDetailsModal
            messageId={message.id}
            isOpen={showReactionDetails}
            onClose={() => setShowReactionDetails(false)}
          />
        )}

        {showConfirmationDetails && roomId && (
          <ConfirmationDetailsModal
            messageId={message.id}
            roomId={roomId}
            isOpen={showConfirmationDetails}
            onClose={() => setShowConfirmationDetails(false)}
          />
        )}

        {showReaderList && roomId && (
          <ReaderListModal
            roomId={roomId}
            messageCreatedLong={
              message.createdOn ? Math.floor(new Date(message.createdOn).getTime() / 1000) : undefined
            }
            isOpen={showReaderList}
            onClose={() => setShowReaderList(false)}
          />
        )}
      </div>
    </div>
  );
};

export default MessageRenderer;
